import {
  Outlet,
  useMatches,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Menu, PanelRightOpen, X } from "lucide-react";
import { Dialog } from "radix-ui";
import { useEffect, useRef, useState } from "react";

import { Logo } from "@/app/Logo";
import { consumeSignOutIntent, isViewTab } from "@/api/session";
import { VIEW_ENDED_PATH } from "@/api/viewStore";
import { pageTitle } from "@/brand";
import { platformRole } from "@/app/registry";
import { useSession } from "@/app/session";
import { Button, Tooltip } from "@/components/ui";
import { AiMatch } from "@/pages/ai-match/AiMatch";
import { AlertBell } from "@/pages/alerts/AlertBell";
import { AlertCards } from "@/pages/alerts/AlertCard";
import { AlertCenterProvider } from "@/pages/alerts/AlertCenter";
import { AwayPopup } from "@/pages/alerts/AwayPopup";

import { EmailBanner } from "./EmailBanner";
import { PlanBanner, PlanGate } from "./PlanBanner";
import { usePlanInactive } from "./planAccess";
import { RoleGate } from "./RoleGate";
import { GlobalSearch } from "./GlobalSearch";
import { Nav } from "./Nav";
import { MarketTicker } from "./MarketTicker";
import { ThemeToggle } from "./ThemeToggle";
import { useSubNav } from "./sections";
import { SubNav } from "./TopNav";
import { UserMenu } from "./UserMenu";
import { useDock } from "./useDock";
import { ViewBanner } from "./ViewBanner";
import { ConsoleNav, ConsoleTabs } from "./ConsoleNav";
import { isConsolePath } from "./consolePages";
import { useMenuLayout, useSidebarCollapsed } from "./layoutPref";
import { Breadcrumb, BottomBar, MarketStatus } from "./ShellBits";
import { Sidebar } from "./Sidebar";
import { TopMenu } from "./TopMenu";
import { WatchDock } from "./WatchDock";

/**
 * Signed-in layout, like a trading terminal: a top bar (logo, menus, search,
 * theme, account) and the two-row market ticker stay on top; pages get the full
 * width, with an optional watchlist docked on the left on wide screens. Phones
 * get a menu button that opens the page list in a drawer.
 */
export function AppShell() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const matches = useMatches();
  const title = [...matches].reverse().find((m) => m.staticData?.title)
    ?.staticData.title;
  const [drawer, setDrawer] = useState(false);
  const [dockOpen, setDock] = useDock();
  const tabs = useSubNav().show;
  // Employees (V036) use only the sales console: no market data around it.
  const staff = platformRole(session.user?.role) === "employee";
  // A member whose trial or membership ended sees Account and Payment only:
  // no market data, search or alerts around them (the API refuses those).
  const inactive = usePlanInactive();
  const bare = staff || inactive;
  // the Watchlist page is the list itself, so no dock beside it
  const dock =
    dockOpen && !bare && !location.pathname.startsWith("/app/watchlist");

  useEffect(() => {
    document.title = pageTitle(title);
  }, [title]);

  // The docked watchlist sticks just under the top bar, ticker and plan
  // banner; their height changes with the banner, so measure it.
  const top = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = top.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const ro = new ResizeObserver(() =>
      root.style.setProperty(
        "--shell-top",
        // in the app the bar sits under the status bar (0 on the website)
        `calc(${el.offsetHeight}px + var(--safe-top))`,
      ),
    );
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--shell-top");
    };
  }, []);

  // Signed out while here (refresh failed, sign-out, another tab): back to
  // sign-in once, remembering the page. The guard stops a second navigation
  // while the first is still under way (which would nest ?redirect= forever).
  const leaving = useRef(false);
  useEffect(() => {
    if (session.status !== "signed-out") {
      leaving.current = false;
      return;
    }
    if (leaving.current || location.pathname === "/login") return;
    leaving.current = true;
    // an admin's view tab (V044) that ended: never the sign-in page here
    if (isViewTab()) {
      void navigate({ to: VIEW_ENDED_PATH, replace: true });
      return;
    }
    // a deliberate sign-out goes to the public home page, like any website
    if (consumeSignOutIntent()) {
      void navigate({ to: "/", replace: true });
      return;
    }
    void navigate({
      to: "/login",
      search: { redirect: location.href },
      replace: true,
    });
  }, [session.status, navigate, location]);

  const [layout] = useMenuLayout();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const inConsole = isConsolePath(location.pathname);
  const side = layout === "side";

  const drawerEl = (
    <Dialog.Root open={drawer} onOpenChange={setDrawer}>
      <Dialog.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-label="Open menu"
        >
          <Menu />
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 lg:hidden" />
        <Dialog.Content className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-surface pt-[var(--safe-top)] pb-[var(--safe-bottom)] pl-[var(--safe-left)] shadow-card lg:hidden">
          <div className="flex h-14 items-center justify-between px-4">
            <Logo />
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close menu">
                <X />
              </Button>
            </Dialog.Close>
          </div>
          <Dialog.Title className="sr-only">Menu</Dialog.Title>
          <Dialog.Description className="sr-only">Pages</Dialog.Description>
          <div className="flex-1 overflow-y-auto px-2 py-3">
            <Nav onNavigate={() => setDrawer(false)} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );

  const tools = (
    <div className="ml-auto flex min-w-0 items-center gap-1.5">
      {bare ? null : <GlobalSearch />}
      {bare ? null : <MarketStatus />}
      {!dockOpen && !bare ? (
        <Tooltip content="Show watchlist">
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:inline-flex"
            aria-label="Show watchlist"
            onClick={() => setDock(true)}
          >
            <PanelRightOpen />
          </Button>
        </Tooltip>
      ) : null}
      {/* the bell stays for a member with no active plan: Market Radar and the
          Daily brief may be sent to them (Admin → Content, rai.delivery.grid) */}
      {staff ? null : <AlertBell />}
      <ThemeToggle />
      <UserMenu />
    </div>
  );

  const header = side ? (
    <header className="flex h-16 items-center gap-2 border-b border-border bg-surface/90 px-3 backdrop-blur sm:px-6">
      {drawerEl}
      <div className="min-w-0 shrink-0 lg:hidden">
        <Logo withName={false} />
      </div>
      <Breadcrumb className="hidden md:block" />
      {tools}
    </header>
  ) : (
    <header
      data-theme="dark"
      className="flex h-16 items-center gap-3 bg-gradient-to-r from-[#0a1230] to-[#13205a] px-3 text-[#e9eefc] sm:px-6"
    >
      {drawerEl}
      <div className="mr-2 min-w-0 shrink-0">
        <Logo className="[&>span]:text-white" />
      </div>
      <TopMenu />
      {tools}
    </header>
  );

  const body = (
    <>
      <EmailBanner />
      {inConsole ? (
        <ConsoleTabs className={side ? "lg:hidden" : undefined} />
      ) : side ? null : (
        <SubNav />
      )}
      <div className="flex min-w-0 flex-1">
        {inConsole && side ? <ConsoleNav /> : null}
        <main className="mx-auto w-full max-w-[1600px] min-w-0 px-4 pt-5 pb-24 sm:px-6 sm:pt-6 lg:pb-8">
          {/* with section tabs the active tab names the page, as on Angel One */}
          {title && !inConsole ? (
            <h1
              className={
                (tabs && !side) || location.pathname === "/app/home"
                  ? "sr-only"
                  : "mb-5 font-display text-[1.75rem] leading-tight font-bold sm:text-[2rem]"
              }
            >
              {title}
            </h1>
          ) : null}
          <PlanGate>
            <RoleGate>
              <Outlet />
            </RoleGate>
          </PlanGate>
        </main>
        {dock ? (
          <div className="hidden w-[18rem] shrink-0 border-l border-border bg-surface xl:block">
            <div className="sticky top-[var(--shell-top)] h-[calc(100dvh-var(--shell-top))]">
              <WatchDock onClose={() => setDock(false)} />
            </div>
          </div>
        ) : null}
      </div>
      <footer className="border-t border-border px-6 py-3 pb-20 text-2xs text-subtle lg:pb-3">
        Data: NSE, BSE, MCX, Angel One · Charts: TradingView Lightweight Charts
      </footer>
      <BottomBar onMenu={() => setDrawer(true)} />
    </>
  );

  return (
    <AlertCenterProvider enabled={!bare}>
      <div className="flex min-h-safe">
        {side ? (
          <Sidebar
            rail={inConsole || collapsed}
            canToggle={!inConsole}
            onToggle={() => setCollapsed(!collapsed)}
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <div ref={top} className="sticky top-[var(--safe-top)] z-30">
            <ViewBanner />
            {header}
            <PlanBanner />
            {bare ? null : <MarketTicker />}
          </div>
          {body}
        </div>
        {/* new alerts as cards (top right) and, on return, what was missed */}
        {bare ? null : <AlertCards />}
        {bare ? null : <AwayPopup />}
        {/* off unless the API says it's on for this person (docs/ai-match.md) */}
        <AiMatch enabled={!bare} menuLeft={!side ? "" : inConsole || collapsed ? "lg:left-[92px]" : "lg:left-[272px]"} />
      </div>
    </AlertCenterProvider>
  );
}
